"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "underline-offset-4 hover:underline text-primary",
        gradient: "bg-gradient-to-r from-blue-700 to-blue-500 text-white hover:brightness-110",
        "gradient-outline": "border-2 border-transparent bg-gradient-to-r from-blue-700 to-blue-500 [background-clip:padding-box] relative after:absolute after:inset-0 after:-z-10 after:p-[2px] after:bg-gradient-to-r after:from-blue-700 after:to-blue-500 after:rounded-md text-white hover:bg-accent/10",
        "blue-outline": "border-2 border-blue-medium bg-transparent text-blue-medium hover:bg-blue-medium hover:text-white transition-all duration-300 ease-in-out",
        "blue-solid": "bg-blue-medium text-white hover:bg-blue-dark transition-colors duration-300",
        "blue-ghost": "text-blue-medium hover:bg-blue-light hover:text-blue-dark transition-colors duration-300",
      },
      size: {
        default: "h-10 py-2 px-4",
        sm: "h-9 px-3 rounded-md",
        lg: "h-11 px-8 rounded-md text-base",
        xl: "h-12 px-10 rounded-md text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isAnimated?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(  ({ className, variant, size, asChild = false, isAnimated = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    if (isAnimated) {
      // Extract React-specific event handlers to avoid type conflicts with Framer Motion
      const {
        onDrag, onDragStart, onDragEnd, onDragEnter, onDragExit, 
        onDragLeave, onDragOver, ...motionProps 
      } = props as any;
      
      return (
        <motion.button
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref as React.Ref<HTMLButtonElement>}
          whileTap={{ scale: 0.98 }}
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
          {...motionProps}
        />
      );
    }return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };